import { useCallback, useEffect, useState } from 'react'
import { Spinner } from 'components'
import { useTranslation } from 'react-i18next'
import UnvDrawer from 'components/UnvDialog/UnvDrawer'
import HistoryViewer from 'components/HistoryViewer/HistoryViewer'
import {
  listStatements,
  getStatement,
  signStatement,
  closeStatement,
} from 'service/GradebookService'
import { listSemesters } from 'service/AcademicService'
import styles from '../AdminFullPage.module.scss'

function StatementsTab({ notify }) {
  const { t } = useTranslation('common')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    semester: '',
    group: '',
    subject: '',
    status: '',
  })
  const [semesters, setSemesters] = useState([])

  const [drawer, setDrawer] = useState(null)
  const [drawerTab, setDrawerTab] = useState('main')

  const load = useCallback(() => {
    setLoading(true)
    const params = {}
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v != null) params[k] = v
    })
    listStatements(params)
      .then((res) => {
        const data = res?.data || res
        setItems(Array.isArray(data) ? data : data?.results || [])
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [filters])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    listSemesters()
      .then((res) => {
        const data = res?.data || res
        setSemesters(Array.isArray(data) ? data : data?.results || [])
      })
      .catch(() => setSemesters([]))
  }, [])

  const openDrawer = (id) => {
    setDrawerTab('main')
    getStatement(id)
      .then((res) => setDrawer(res?.data || res))
      .catch(() => setDrawer(null))
  }

  const onSign = (id) => {
    signStatement(id)
      .then(() => {
        notify(t('admin.fullPage.statements_signed'), 'success')
        load()
        if (drawer?.id === id) openDrawer(id)
      })
      .catch(() => notify(t('admin.fullPage.statements_sign_error'), 'error'))
  }

  const onClose = (id) => {
    closeStatement(id)
      .then(() => {
        notify(t('admin.fullPage.statements_closed'), 'success')
        load()
        if (drawer?.id === id) openDrawer(id)
      })
      .catch(() => notify(t('admin.fullPage.statements_close_error'), 'error'))
  }

  return (
    <div>
      <h2 className={styles.section_title}>{t('admin.fullPage.statements_title')}</h2>

      <div className={styles.toolbar}>
        <select
          className="unv-select"
          value={filters.semester}
          onChange={(e) =>
            setFilters((s) => ({ ...s, semester: e.target.value }))
          }
        >
          <option value="">{t('admin.fullPage.statements_all_semesters')}</option>
          {semesters.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title || s.name || t('admin.fullPage.statements_semester_n', { n: s.id })}
            </option>
          ))}
        </select>
        <input
          className="unv-input"
          placeholder={t('admin.fullPage.field_group')}
          value={filters.group}
          onChange={(e) => setFilters((s) => ({ ...s, group: e.target.value }))}
        />
        <input
          className="unv-input"
          placeholder={t('admin.fullPage.statements_subject')}
          value={filters.subject}
          onChange={(e) =>
            setFilters((s) => ({ ...s, subject: e.target.value }))
          }
        />
        <select
          className="unv-select"
          value={filters.status}
          onChange={(e) => setFilters((s) => ({ ...s, status: e.target.value }))}
        >
          <option value="">{t('admin.fullPage.statements_all_statuses')}</option>
          <option value="open">{t('admin.fullPage.statements_status_open')}</option>
          <option value="signed">{t('admin.fullPage.statements_status_signed')}</option>
          <option value="closed">{t('admin.fullPage.statements_status_closed')}</option>
        </select>
      </div>

      {loading ? (
        <div className={styles.spinner_box}>
          <Spinner.Center />
        </div>
      ) : items.length === 0 ? (
        <div className="unv-empty">{t('admin.fullPage.statements_not_found')}</div>
      ) : (
        <table className="unv-table unv-table--clickable">
          <thead>
            <tr>
              <th>ID</th>
              <th>{t('admin.fullPage.field_group')}</th>
              <th>{t('admin.fullPage.statements_subject')}</th>
              <th>{t('admin.fullPage.statements_semester')}</th>
              <th>{t('admin.fullPage.statements_teacher')}</th>
              <th>{t('common.status')}</th>
              <th style={{ width: 220 }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td onClick={() => openDrawer(it.id)}>{it.id}</td>
                <td onClick={() => openDrawer(it.id)}>
                  {it.group?.name || it.group || '—'}
                </td>
                <td onClick={() => openDrawer(it.id)}>
                  {it.subject?.title || it.subject || '—'}
                </td>
                <td onClick={() => openDrawer(it.id)}>
                  {it.semester?.title || it.semester || '—'}
                </td>
                <td onClick={() => openDrawer(it.id)}>
                  {it.teacher?.full_name || it.teacher || '—'}
                </td>
                <td onClick={() => openDrawer(it.id)}>
                  <span className="unv-badge">{it.status || '—'}</span>
                </td>
                <td>
                  {it.status !== 'closed' ? (
                    <>
                      <button
                        type="button"
                        className="unv-btn unv-btn--ghost unv-btn--sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          onSign(it.id)
                        }}
                        style={{ marginRight: 6 }}
                      >
                        {t('admin.fullPage.statements_sign')}
                      </button>
                      <button
                        type="button"
                        className="unv-btn unv-btn--sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          onClose(it.id)
                        }}
                      >
                        {t('common.close')}
                      </button>
                    </>
                  ) : (
                    <span className="unv-badge unv-badge--muted">{t('admin.fullPage.statements_status_closed')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <UnvDrawer
        open={!!drawer}
        title={t('admin.fullPage.statements_card_no', { n: drawer?.id || '' })}
        onClose={() => setDrawer(null)}
      >
        {drawer ? (
          <div>
            <div className="unv-tabs">
              <button
                type="button"
                className={`unv-tab ${drawerTab === 'main' ? 'unv-tab--active' : ''}`}
                onClick={() => setDrawerTab('main')}
              >
                {t('admin.fullPage.drawer_tab_main')}
              </button>
              <button
                type="button"
                className={`unv-tab ${drawerTab === 'rows' ? 'unv-tab--active' : ''}`}
                onClick={() => setDrawerTab('rows')}
              >
                {t('admin.fullPage.statements_tab_students')}
              </button>
              <button
                type="button"
                className={`unv-tab ${drawerTab === 'history' ? 'unv-tab--active' : ''}`}
                onClick={() => setDrawerTab('history')}
              >
                {t('admin.fullPage.drawer_tab_history')}
              </button>
            </div>

            {drawerTab === 'main' ? (
              <div className={styles.kv}>
                <div className={styles.kv_key}>ID</div>
                <div>{drawer.id}</div>
                <div className={styles.kv_key}>{t('admin.fullPage.field_group')}</div>
                <div>{drawer.group?.name || drawer.group || '—'}</div>
                <div className={styles.kv_key}>{t('admin.fullPage.statements_subject')}</div>
                <div>{drawer.subject?.title || drawer.subject || '—'}</div>
                <div className={styles.kv_key}>{t('admin.fullPage.statements_semester')}</div>
                <div>{drawer.semester?.title || drawer.semester || '—'}</div>
                <div className={styles.kv_key}>{t('admin.fullPage.statements_teacher')}</div>
                <div>{drawer.teacher?.full_name || drawer.teacher || '—'}</div>
                <div className={styles.kv_key}>{t('common.status')}</div>
                <div>{drawer.status || '—'}</div>
              </div>
            ) : null}

            {drawerTab === 'rows' ? (
              Array.isArray(drawer.rows) && drawer.rows.length > 0 ? (
                <table className="unv-table">
                  <thead>
                    <tr>
                      <th>{t('admin.fullPage.statements_th_student')}</th>
                      <th>{t('admin.fullPage.statements_th_grade')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drawer.rows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.student?.full_name || r.student || '—'}</td>
                        <td>{r.mark || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="unv-empty">{t('admin.fullPage.dashboard_no_records')}</div>
              )
            ) : null}

            {drawerTab === 'history' ? (
              <HistoryViewer
                module="gradebook"
                targetId={drawer.id}
                targetType="statement"
                title={t('admin.fullPage.statements_history_title')}
              />
            ) : null}
          </div>
        ) : null}
      </UnvDrawer>
    </div>
  )
}

export default StatementsTab
